import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAudienceShowDateLabel, getShowDetailsPath, isPassportShow, isPastShow, orderedValenciaGigs, partitionValenciaGigs, sortValenciaGigs } from "../../data/gigs";
import { getHeaderAuthCta, getPassportCta, useAuthSession } from "../auth/useAuthSession";
import { loadPublishedShows } from "./audiencePassportApi";
import "./gigathlon.css";

const GigathlonWordmark = () => (
  <span className="gigathlon-wordmark" aria-label="Gigathlon">
    G<span className="gigathlon-wordmark__mic" aria-hidden="true">🎤</span>g<span>athlon</span>
  </span>
);

const ShowCard = ({ show }) => {
  const isCompleted = isPastShow(show);
  return (
    <article className="step gig-card">
      <img className="gig-card__image" src={show.image} srcSet={show.imageSrcSet || undefined} sizes="(max-width: 680px) 92vw, (max-width: 1100px) 46vw, 360px" alt={`${show.title} comedy show`} />
      <div className="gig-card__body">
        <span className={`gig-card__status ${!isCompleted && isPassportShow(show) ? "gig-card__status--participating" : ""}`}>
          {isCompleted ? "Completed" : isPassportShow(show) ? "Passport event" : "Listing only · No stamp"}
        </span>
        <p className="gig-card__meta">{show.language} · {getAudienceShowDateLabel(show)}</p>
        <h3>{show.title}</h3>
        <p>{show.description}</p>
      </div>
      <Link className="gig-card__cta" to={getShowDetailsPath(show)}>View show</Link>
    </article>
  );
};

const ValenciaPassportPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shows, setShows] = useState(orderedValenciaGigs);
  const { user } = useAuthSession();
  const headerCta = getHeaderAuthCta(user);
  const passportCta = getPassportCta(user);
  const { upcoming, past } = partitionValenciaGigs(shows);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    let active = true;
    loadPublishedShows().then((response) => {
      if (!active || !response.shows?.length) return;
      const merged = new Map(orderedValenciaGigs.map((show) => [show.id, show]));
      response.shows.forEach((show) => merged.set(show.id, { ...merged.get(show.id), ...show, image: show.imageUrl || merged.get(show.id)?.image }));
      setShows(sortValenciaGigs(Array.from(merged.values())));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <div className="valencia-page">
      <header className={`passport-topbar ${isMobileMenuOpen ? "passport-topbar--menu-open" : ""}`}>
        <Link to="/" className="gigathlon-wordmark"><GigathlonWordmark /></Link>
        <nav className="passport-topbar__nav" aria-label="Valencia passport navigation">
          <Link to="/">Home</Link><a href="#valencia-shows">Shows</a><Link to="/passport">My Passport</Link>
        </nav>
        <div className="passport-topbar__actions">
          <Link className="passport-topbar__submit" to={headerCta.to}>{headerCta.label}</Link>
          <button className="passport-topbar__menu-button" type="button" aria-expanded={isMobileMenuOpen} aria-controls="passport-mobile-menu" aria-label="Open Valencia navigation menu" onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}>
            <span /><span /><span />
          </button>
        </div>
        <nav className="passport-mobile-menu" id="passport-mobile-menu" aria-label="Valencia mobile navigation">
          <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          <a href="#valencia-shows" onClick={() => setIsMobileMenuOpen(false)}>Shows</a>
          <Link to="/passport" onClick={() => setIsMobileMenuOpen(false)}>My Passport</Link>
        </nav>
      </header>

      <main>
        <section className="valencia-hero">
          <div className="courses-container">
            <p className="valencia-hero__eyebrow">Early Access · Powered by Gigathlon</p>
            <h1>Valencia Comedy Passport</h1>
            <p>Go to participating comedy shows and show your passport QR to the organizer to collect a digital stamp. Then use your passport to discover where to go next.</p>
            <Link className="courses-start-button" to={passportCta.to}>{passportCta.label}</Link>
          </div>
        </section>

        <section className="courses-section gig-listing" id="valencia-shows">
          <div className="courses-container">
            <div className="audience-listing-heading">
              <p>Valencia comedy guide</p>
              <h2>Choose your next comedy night</h2>
              <span>Book directly with each venue. Passport events also let you check in and collect a verified digital stamp.</span>
            </div>
            <div className="steps-grid gig-card-grid">
              {upcoming.map((show) => <ShowCard key={show.id} show={show} />)}
            </div>
            {!upcoming.length ? <p className="gig-listing__empty">New Valencia dates are being added. Check back soon.</p> : null}
          </div>
        </section>

        {past.length ? <section className="courses-section gig-listing gig-listing--past" id="past-shows">
          <div className="courses-container">
            <div className="audience-listing-heading">
              <p>Passport archive</p>
              <h2>Past comedy nights</h2>
              <span>See the rooms and shows that have already been part of Valencia's comedy calendar.</span>
            </div>
            <div className="steps-grid gig-card-grid">
              {past.map((show) => <ShowCard key={show.id} show={show} />)}
            </div>
          </div>
        </section> : null}

        <section className="audience-scan-note">
          <div className="courses-container">
            <p>At the venue</p>
            <h2>Look for the Comedy Passport QR code.</h2>
            <p>Scan it while you are at the show. If you are not signed in, we will bring you straight back after mobile verification.</p>
          </div>
        </section>
      </main>

      <footer className="gigathlon-footer">
        <div className="gigathlon-container gigathlon-footer__layout">
          <div><GigathlonWordmark /><p>Go to more comedy. Collect stamps. Get rewarded.</p></div>
          <div><h3>Your passport</h3><Link to="/passport">View my stamps</Link><Link to="/">How it works</Link></div>
          <div><h3>Support</h3><a href="https://wa.me/447739343143?text=Hi%2C%20I%20would%20need%20support%20regarding%20the%20Valencia%20Comedy%20Passport" target="_blank" rel="noreferrer">WhatsApp support</a></div>
        </div>
      </footer>
    </div>
  );
};

export default ValenciaPassportPage;
